import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { adminAPI } from '@/services/api';
import { useEmailTemplates, useEmailTest } from '@/hooks/email';
import { useNotification } from '@/hooks/common';

const EmailTemplateManagement: React.FC = () => {
  const { success, setSuccess } = useNotification({ duration: 3000 });
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [templateTestEmail, setTemplateTestEmail] = useState('');

  const {
    list,
    editor,
    loadingState,
    error,
    setError,
    refs,
    handlers,
  } = useEmailTemplates();

  const { testing: templateTestLoading, testResult, setTestResult, testEmailTemplate } = useEmailTest();

  const handleSave = async () => {
    try {
      const message = await handlers.save();
      if (message) {
        setSuccess(message);
      }
    } catch (_error) {
      // Error is handled by hook
    }
  };

  const handleTest = async () => {
    if (!editor.selected || !templateTestEmail) {
      setError('수신자 이메일을 입력해주세요.');
      return;
    }

    const success = await testEmailTemplate(editor.selected, templateTestEmail);
    if (success) {
      setTemplateTestEmail('');
    }
  };

  const handleReset = async () => {
    if (resetConfirmText !== 'DELETE') {
      setError('정확히 "DELETE"를 입력해주세요.');
      return;
    }

    setResetting(true);
    setError('');
    setSuccess('');
    try {
      const result = await adminAPI.resetAllEmailTemplates();
      setSuccess(result.message || '모든 템플릿이 기본값으로 초기화되었습니다.');
      setResetDialogOpen(false);
      setResetConfirmText('');
      await handlers.loadTemplates();
      if (editor.selected) {
        await handlers.select(editor.selected);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || '템플릿 초기화에 실패했습니다.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>이메일 템플릿 관리</CardTitle>
              <CardDescription className="mt-1.5">시스템에서 사용되는 다양한 이메일 템플릿을 수정하고 관리합니다</CardDescription>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setResetDialogOpen(true)}
              title="템플릿 초기화"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {error && (
              <AlertMessage 
                type="error" 
                message={error} 
                onClose={() => setError('')} 
              />
            )}

            {success && (
              <AlertMessage 
                type="success" 
                message={success} 
                onClose={() => setSuccess('')} 
              />
            )}

            <div className="grid grid-cols-3 gap-4" style={{ height: '600px' }}>
              {/* 템플릿 선택 컬럼 */}
              <div className="flex flex-col h-full min-h-0">
                <div className="h-10 flex items-center mb-2 flex-shrink-0">
                  <Label className="text-base font-semibold">템플릿 선택</Label>
                </div>
                <div className="flex-1 border border-gray-300 rounded-md overflow-y-auto min-h-0">
                  {list.loading ? (
                    <div className="p-4 text-center text-gray-500">템플릿을 불러오는 중...</div>
                  ) : list.templates.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">템플릿이 없습니다.</div>
                  ) : (
                    list.templates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handlers.select(template.id)}
                        className={`w-full text-left px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors ${
                          editor.selected === template.id ? 'bg-indigo-50 border-l-4 border-l-indigo-600' : ''
                        }`}
                      >
                        <div className="font-medium text-sm">{template.name}</div>
                        <div className="text-xs text-gray-500 mt-1">{template.description}</div>
                        <div className="text-xs text-gray-400 mt-1">
                          변수: {template.variables?.join(', ') || '없음'}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* 템플릿 편집 컬럼 */}
              <div className="flex flex-col h-full min-h-0">
                <div className="h-10 flex items-center justify-between mb-2 flex-shrink-0">
                  <Label className="text-base font-semibold">템플릿 편집</Label>
                  {editor.selected && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handlers.previewTemplate}
                        disabled={loadingState.preview || !editor.content}
                      >
                        {loadingState.preview ? '로딩...' : '미리보기'}
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={loadingState.content || !editor.content}
                      >
                        {loadingState.content ? '저장 중...' : '저장'}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex-1 flex flex-col min-h-0">
                  {editor.selected ? (
                    <>
                      {loadingState.content ? (
                        <div className="border border-gray-300 rounded-md flex-1 flex items-center justify-center text-gray-500 min-h-[400px]">
                          템플릿을 불러오는 중...
                        </div>
                      ) : (
                        <>
                          <div className="flex-1 min-h-0 flex flex-col">
                            <div
                              ref={refs.editor}
                              contentEditable
                              suppressContentEditableWarning
                              spellCheck={false}
                              onInput={(e) => {
                                if (refs.isComposing.current) return;
                                const text = e.currentTarget.textContent || '';
                                handlers.handleContentChange(text);
                              }}
                              onCompositionStart={() => {
                                refs.isComposing.current = true;
                              }}
                              onCompositionEnd={(e) => {
                                refs.isComposing.current = false;
                                const text = e.currentTarget.textContent || '';
                                handlers.handleContentChange(text);
                                if (editor.selected) {
                                  handlers.applyHighlighting(text, editor.selected);
                                }
                              }}
                              onBlur={(e) => {
                                const text = e.currentTarget.textContent || '';
                                handlers.handleContentChange(text);
                                if (editor.selected) {
                                  handlers.applyHighlighting(text, editor.selected);
                                }
                              }}
                              className="flex-1 min-h-0 border border-gray-300 rounded-md px-3 py-2 font-mono text-sm overflow-auto resize-none outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              style={{
                                whiteSpace: 'pre',
                                minHeight: '400px',
                              }}
                            />
                          </div>
                          <div className="flex items-center gap-2 mt-2 flex-shrink-0">
                            <Input
                              type="email"
                              value={templateTestEmail}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateTestEmail(e.target.value)}
                              placeholder="테스트 메일 수신자 이메일"
                              className="flex-1"
                              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (e.key === 'Enter') {
                                  handleTest();
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleTest}
                              disabled={templateTestLoading || !templateTestEmail || !editor.content}
                            >
                              {templateTestLoading ? '전송 중...' : '테스트 발송'}
                            </Button>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="border border-gray-300 rounded-md flex-1 flex items-center justify-center text-gray-500 min-h-[400px]">
                      왼쪽에서 템플릿을 선택하세요
                    </div>
                  )}
                </div>
              </div>

              {/* 미리보기 컬럼 */}
              <div className="flex flex-col h-full min-h-0">
                <div className="h-10 flex items-center mb-2 flex-shrink-0">
                  <Label className="text-base font-semibold">미리보기</Label>
                </div>
                <div className="flex-1 border border-gray-300 rounded-md bg-white overflow-auto min-h-0">
                  {editor.preview ? (
                    <iframe
                      srcDoc={editor.preview}
                      className="w-full h-full border-0"
                      title="Email template preview"
                      sandbox="allow-same-origin"
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400 p-4">
                      미리보기를 생성하려면 "미리보기" 버튼을 클릭하세요
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 테스트 메일 결과 모달 */}
      <Dialog open={testResult.isOpen}>
        <DialogContent onClose={() => setTestResult({ ...testResult, isOpen: false })}>
          <DialogHeader>
            <DialogTitle className={testResult.success ? 'text-green-600' : 'text-red-600'}>
              {testResult.success ? '✅ 테스트 메일 전송 성공' : '❌ 테스트 메일 전송 실패'}
            </DialogTitle>
            <DialogDescription>
              {testResult.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setTestResult({ ...testResult, isOpen: false })}>
              확인
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* 템플릿 초기화 확인 다이얼로그 */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">⚠️ 템플릿 초기화 경고</DialogTitle>
            <DialogDescription>
              기존에 저장한 설정들이 모두 사라지고 다시 기본 템플릿으로 초기화 됩니다.
              <br />
              <br />
              이 작업은 되돌릴 수 없습니다. 계속하시려면 아래에 <strong>DELETE</strong>를 입력해주세요.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reset_confirm">확인 입력</Label>
              <Input
                id="reset_confirm"
                value={resetConfirmText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResetConfirmText(e.target.value)}
                placeholder="DELETE"
                className="font-mono"
                disabled={resetting}
              />
            </div>
            {error && (
              <div className="text-sm text-red-600">{error}</div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResetDialogOpen(false);
                setResetConfirmText('');
                setError('');
              }}
              disabled={resetting}
            >
              취소
            </Button>
            <Button
              variant="destructive"
              onClick={handleReset}
              disabled={resetting || resetConfirmText !== 'DELETE'}
            >
              {resetting ? '초기화 중...' : '초기화'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmailTemplateManagement;
